import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type Lang = "nl" | "en";

type Ctx = { lang: Lang; setLang: (l: Lang) => void };

const LanguageContext = createContext<Ctx>({ lang: "nl", setLang: () => {} });

const STORAGE_KEY = "startburo-lang";

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [lang, setLangState] = useState<Lang>(() => {
    if (typeof window === "undefined") return "nl";
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "en" || stored === "nl") return stored;
    // First visit: detect browser language. Default to English unless Dutch.
    const browserLang = navigator.language?.toLowerCase() || "";
    return browserLang.startsWith("nl") ? "nl" : "en";
  });

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const setLang = (l: Lang) => {
    setLangState(l);
    try {
      window.localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* ignore */
    }
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);

/**
 * Inline translation helper.
 * Usage: const t = useT(); <p>{t("Hallo", "Hello")}</p>
 */
export const useT = () => {
  const { lang } = useLanguage();
  return (nl: string, en: string) => (lang === "nl" ? nl : en);
};

/**
 * Pick between two arbitrary values (useful for JSX fragments).
 */
export const useTx = () => {
  const { lang } = useLanguage();
  return <T,>(nl: T, en: T): T => (lang === "nl" ? nl : en);
};